import { transliterateChar } from "./cp437";
import { type Block, forPrinter, type MonoBitmap, type PaperWidth, withLogos } from "./doc";
import type { PrintDocument } from "./document";
import { encodeBlocks } from "./escpos";
import type { PrinterProfile } from "./print";

/**
 * Text mode or image mode, per job (printing.md, Text mode and image mode).
 * A printer is set to one of them; a text mode printer still prints a job in
 * image mode when the document holds characters that CP437 cannot show even
 * after transliteration (₹ becomes "Rs.", é stays é, but Hindi or Assamese
 * would print as "?"). QR codes and barcodes are native commands in both modes.
 */

export type PrintMode = "text" | "image";

/** Text a printer draws with its own font: text, table cells and rule characters. */
function* texts(blocks: Block[]): Generator<string> {
	for (const b of blocks) {
		if (b.t === "text") yield b.text;
		else if (b.t === "row") for (const c of b.cols) yield c.text;
		else if (b.t === "rule" && b.ch) yield b.ch;
		else if (b.t === "byWidth") {
			yield* texts(b.w58);
			yield* texts(b.w80);
		}
	}
}

/**
 * The distinct characters that text mode cannot print, in the order they
 * first appear. Line breaks are layout, not characters, and do not count.
 */
export function charactersOutsideCp437(blocks: Block[]): string[] {
	const found = new Set<string>();
	for (const text of texts(blocks)) {
		for (const ch of text) {
			if (ch === "\n" || ch === "\r" || found.has(ch)) continue;
			if (transliterateChar(ch) === null) found.add(ch);
		}
	}
	return [...found];
}

/** The mode a job prints in on a printer set to `printerMode`. */
export function printModeFor(doc: { blocks: Block[] }, printerMode: PrintMode): PrintMode {
	if (printerMode === "image") return "image";
	return charactersOutsideCp437(doc.blocks).length > 0 ? "image" : "text";
}

/**
 * Platform code that draws text with a system font (canvas on the web,
 * Android Canvas in the native module): it gets the blocks for one printer
 * and returns them with text, rows and rules replaced by `bitmap` blocks of
 * the paper's width. QR codes, barcodes, feeds, cuts, beeps and drawer pulses
 * are passed through untouched, so they stay native commands, and so are
 * `bitmap` blocks (a logo, already dithered). It never sees `byWidth` or
 * `logo` blocks: `encodeJob` resolves them first.
 */
export type Rasteriser = (blocks: Block[], paper: PaperWidth) => Promise<Block[]>;

export type EncodedJob = { mode: PrintMode; bytes: Uint8Array };

/**
 * The bytes of one job for one printer: the document resolved for its paper
 * and capabilities, in the mode `printModeFor` picks. Image mode needs the
 * platform's rasteriser.
 */
export async function encodeJob(
	doc: Pick<PrintDocument, "blocks">,
	printer: PrinterProfile & { mode: PrintMode },
	rasterise?: Rasteriser,
	options: {
		/** The logo's bitmap for this paper (`withLogos`); without it logos are left out. */
		logo?: (fileId: string) => MonoBitmap | null;
	} = {},
): Promise<EncodedJob> {
	const withLogo = withLogos(doc.blocks, printer.paper, options.logo ?? (() => null));
	const blocks = forPrinter(withLogo, printer);
	const mode = printModeFor({ blocks }, printer.mode);
	if (mode === "text") return { mode, bytes: encodeBlocks(blocks, printer.paper) };
	if (!rasterise) throw new Error("Image mode needs a rasteriser on this device");
	const drawn = await rasterise(blocks, printer.paper);
	return { mode, bytes: encodeBlocks(drawn, printer.paper) };
}
